import { useMemo } from 'react';

import { dateUtils } from 'shared_DEPRECATED/utils/date';

import { useMomentumQuery } from 'features/momentum/hooks/query/useMomentum';

import { useSidebarContext } from 'shared/components/Sidebar/context';

export const useWeekChartData = () => {
  const { dateRange } = useSidebarContext() as {
    dateRange?: { startDate: string; endDate: string };
  };

  // Calculate previous week date range
  const previousWeekDateRange = useMemo(() => {
    if (!dateRange) return null;

    const prevWeekStartDate = dateUtils(dateRange.startDate)
      .subtract(dateUtils.WEEK_DAYS_COUNT, 'days')
      .format('YYYY-MM-DD');
    const prevWeekEndDate = dateUtils(dateRange.endDate)
      .subtract(dateUtils.WEEK_DAYS_COUNT, 'days')
      .format('YYYY-MM-DD');

    return {
      startDate: prevWeekStartDate,
      endDate: prevWeekEndDate,
    };
  }, [dateRange]);

  // Fetch current week momentum data
  const { data: currentWeekMomentum = [], isLoading: isCurrentWeekLoading } =
    useMomentumQuery({
      dateRange: dateRange || { startDate: '', endDate: '' },
      options: { enabled: !!dateRange },
    });

  // Fetch previous week momentum data
  const { data: previousWeekMomentum = [], isLoading: isPreviousWeekLoading } =
    useMomentumQuery({
      dateRange: previousWeekDateRange || { startDate: '', endDate: '' },
      options: { enabled: !!previousWeekDateRange },
    });

  const data = useMemo(() => {
    if (!dateRange) return [];

    // Generate all dates for the current week
    const weekDates = Array.from(
      { length: dateUtils.WEEK_DAYS_COUNT },
      (_, index) =>
        dateUtils(dateRange.startDate).add(index, 'days').format('YYYY-MM-DD')
    );

    return weekDates.map((date) => {
      const currentMomentumItem = currentWeekMomentum.find(
        (item) => item.date === date
      );
      const prevWeekDate = dateUtils(date)
        .subtract(dateUtils.WEEK_DAYS_COUNT, 'days')
        .format('YYYY-MM-DD');
      const prevMomentumItem = previousWeekMomentum.find(
        (item) => item.date === prevWeekDate
      );

      return currentMomentumItem
        ? {
            ...currentMomentumItem,
            prevScore: prevMomentumItem?.score,
          }
        : {
            date,
            score: undefined,
            prevScore: prevMomentumItem?.score,
          };
    });
  }, [currentWeekMomentum, previousWeekMomentum, dateRange]);

  const referenceDotData = useMemo(() => {
    return data.find((item, index) => {
      const isNextItemScoreAvailable = Boolean(data[index + 1]?.score);
      return item.score && !isNextItemScoreAvailable;
    });
  }, [data]);

  // Find the latest present date (today or earlier) with momentum data
  const latestPresentDate = useMemo(() => {
    if (!data.length) return null;

    const today = dateUtils().format('YYYY-MM-DD');

    // Filter items that are today or in the past and have score data
    const presentItems = data.filter((item) => {
      const isToday = item.date === today;
      const isPast = item.date < today; // String comparison works for YYYY-MM-DD format
      const hasScore = item.score !== undefined && item.score !== null;

      return (isToday || isPast) && hasScore;
    });

    // Sort by date descending and get the latest
    if (presentItems.length === 0) return null;

    return presentItems.sort((a, b) => b.date.localeCompare(a.date))[0];
  }, [data]);

  const isLoading = isCurrentWeekLoading || isPreviousWeekLoading;

  return {
    data,
    referenceDotData,
    latestPresentDate,
    isLoading,
  };
};
